import { Container, Sprite, Text } from 'pixi.js';
import { assetLoader } from '../core/AssetLoader';

// Nebula data from server
export interface NebulaData {
  id: string;
  name: string;
  x: number;
  y: number;
  radius: number;
  nebulaType: string;
  resourceType: string;
  density: number;
  colorR: number;
  colorG: number;
  colorB: number;
}

export class Nebula extends Container {
  public nebulaId: string;
  public nebulaName: string;
  public nebulaType: string;
  public resourceType: string;
  public nebulaRadius: number;
  public density: number;

  private mainColor: number;
  private nebulaSprite: Sprite;
  private nameText: Text;
  private time: number = 0;

  constructor(data: NebulaData) {
    super();

    this.position.set(data.x, data.y);

    this.nebulaId = data.id;
    this.nebulaName = data.name;
    this.nebulaType = data.nebulaType;
    this.resourceType = data.resourceType;
    this.nebulaRadius = data.radius;
    this.density = data.density;

    // Convert RGB to hex color
    this.mainColor = (data.colorR << 16) | (data.colorG << 8) | data.colorB;

    // Enable sorting by zIndex
    this.sortableChildren = true;

    // Create nebula sprite from loaded texture, tinted to server color
    const texture = assetLoader.getTexture('nebula_cloud')!;
    this.nebulaSprite = new Sprite(texture);
    this.nebulaSprite.anchor.set(0.5);
    this.nebulaSprite.tint = this.mainColor;
    this.nebulaSprite.zIndex = -10;

    // Scale sprite to match the server-defined radius
    // The texture is roughly 400px wide; scale so it covers diameter = radius * 2
    const targetDiameter = this.nebulaRadius * 2;
    const textureWidth = texture.width || 400;
    const scale = targetDiameter / textureWidth;
    this.nebulaSprite.scale.set(scale);
    this.nebulaSprite.alpha = 0.6 + this.density * 0.2;

    this.addChild(this.nebulaSprite);

    // Create nebula name label
    this.nameText = new Text({
      text: this.nebulaName.toUpperCase(),
      style: {
        fontFamily: 'monospace',
        fontSize: 14,
        fill: this.mainColor,
        stroke: { color: 0x0a1628, width: 2 },
      },
    });
    this.nameText.anchor.set(0.5);
    this.nameText.position.set(0, -this.nebulaRadius - 50);
    this.nameText.alpha = 0.7;
    this.nameText.zIndex = 2;
    this.addChild(this.nameText);
  }

  // Check if a point is inside the nebula
  isInside(x: number, y: number): boolean {
    const dx = x - this.x;
    const dy = y - this.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance < this.nebulaRadius;
  }

  // Get collection rate multiplier based on position (higher near center)
  getCollectionMultiplier(x: number, y: number): number {
    const dx = x - this.x;
    const dy = y - this.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance >= this.nebulaRadius) return 0;

    // Higher multiplier closer to center
    const centerProximity = 1 - (distance / this.nebulaRadius);
    return 0.5 + centerProximity * 0.5;
  }

  // Get distance to nebula center
  getDistanceTo(x: number, y: number): number {
    const dx = x - this.x;
    const dy = y - this.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  update(delta: number): void {
    this.time += delta / 1000;

    // Subtle scale breathing
    const breathe = Math.sin(this.time * 0.3) * 0.02 + 1;
    const targetDiameter = this.nebulaRadius * 2;
    const textureWidth = this.nebulaSprite.texture.width || 400;
    const baseScale = targetDiameter / textureWidth;
    this.nebulaSprite.scale.set(baseScale * breathe);
  }

  updateFromServer(data: Partial<NebulaData>): void {
    if (data.name !== undefined) {
      this.nebulaName = data.name;
      this.nameText.text = data.name.toUpperCase();
    }
    if (data.density !== undefined) {
      this.density = data.density;
    }
    if (data.colorR !== undefined && data.colorG !== undefined && data.colorB !== undefined) {
      this.mainColor = (data.colorR << 16) | (data.colorG << 8) | data.colorB;
      this.nebulaSprite.tint = this.mainColor;
      this.nameText.style.fill = this.mainColor;
    }
  }

  // Get position for radar display
  public getPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  destroy(): void {
    this.nebulaSprite.destroy();
    this.nameText.destroy();
    super.destroy();
  }
}
